import { startTransition, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  PaginationType,
  SetPaginationCountType,
} from "src/components/TablePaginationLimitChanger/hooks/useTablePaginationLimitChanger";
import { useSetPaginationCount } from "src/hooks/useSetPaginationCount";
import {
  convertFilterToParams,
  convertOptionsToParams,
  convertStringToParams,
  getFilterParam,
  getOptionParam,
  getStringParam,
} from "src/utils/convertToSearchParams";
import { useGetSalespersonList } from "../api/queries";
import { useGetAccessPermission } from "src/hooks/useGetAccessPermission";
import { AccessResource, ActionType } from "src/types/accessControl";
import { RoutePaths } from "src/pages/routePaths";
import { SelectOptionsType } from "src/types";

export interface FilterType {
  country: SelectOptionsType;
  city: SelectOptionsType;
}

export const salespersonListFilterDefaultValue = {
  country: [],
  city: [],
};

interface UseSalespersonListHandlerProps {
  pagination: PaginationType;
  setCount: SetPaginationCountType;
}

export const salespersonListSortOptions = [
  {
    label: "Latest Added",
    value: "latest-added",
  },
  {
    label: "Earliest Added",
    value: "earliest-added",
  },
  {
    label: "Name A to Z",
    value: "name-a-to-z",
  },
  {
    label: "Name Z to A",
    value: "name-z-to-a",
  },
];

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const salespersonListSortOptionsValue = [
  "latest-added",
  "earliest-added",
  "name-a-to-z",
  "name-z-to-a",
] as const;

export type SalespersonListSortValueType =
  (typeof salespersonListSortOptionsValue)[number];

export const getSalespersonListSortingParams = (
  type: SalespersonListSortValueType
) => {
  let params = {};

  if (type === "latest-added") {
    params = {
      sort_by: "created_at",
      sort_order: "desc",
    };
  } else if (type === "earliest-added") {
    params = {
      sort_by: "created_at",
      sort_order: "asc",
    };
  } else if (type === "name-a-to-z") {
    params = {
      sort_by: "name",
      sort_order: "asc",
    };
  } else if (type === "name-z-to-a") {
    params = {
      sort_by: "name",
      sort_order: "desc",
    };
  }

  return params;
};

const useSalespersonListHandler = ({
  pagination,
  setCount,
}: UseSalespersonListHandlerProps) => {
  const navigate = useNavigate();

  const isAllowToCreate = useGetAccessPermission({
    resource: AccessResource["User Management - Salesperson List"],
    action: ActionType["Create"],
  });

  const [searchParams, setSearchParams] = useSearchParams();

  const initialSearchInputValue = getStringParam(
    searchParams,
    "searchValue",
    ""
  );

  const initialSort = getOptionParam(
    searchParams,
    "sort",
    salespersonListSortOptions.map((o) => o.value),
    salespersonListSortOptions[0].value
  );

  const initialFilter = getFilterParam<FilterType>(
    searchParams,
    "filter",
    salespersonListFilterDefaultValue
  );

  const [searchInputValue, setSearchInputValueState] = useState(
    initialSearchInputValue
  );
  const [sort, setSortState] = useState(initialSort);
  const [filter, setFilterState] = useState<FilterType>(initialFilter);

  const setSearchInputValue = convertStringToParams(
    "searchValue",
    setSearchInputValueState,
    setSearchParams
  );

  const setSort = convertOptionsToParams(
    "sort",
    setSortState as React.Dispatch<React.SetStateAction<any>>,
    setSearchParams
  );

  const setFilter = convertFilterToParams<FilterType>(
    "filter",
    setFilterState,
    setSearchParams,
    (value) =>
      Object.values(value).some((v) =>
        Array.isArray(v) ? v.length > 0 : false
      )
  );

  const { data, isLoading } = useGetSalespersonList({
    ...pagination,
    ...(searchInputValue
      ? { salespersonListNameOrPhoneNumber: searchInputValue }
      : {}),
    sortBy: sort,
    ...filter,
  });

  const handleAdd = () =>
    startTransition(() =>
      navigate(RoutePaths.UserManagementCreateSalespersonList)
    );

  useSetPaginationCount({ meta: data?.meta, setCount });

  return {
    isAllowToCreate,
    isLoading,
    searchInputValue,
    salespersonList: data?.data,
    totalCount: data?.meta.total ?? 0,
    setSearchInputValue,
    sort,
    setSort,
    filter,
    setFilter,
    hasFilter:
      searchInputValue !== "" &&
      Object.values(filter).some((filter) =>
        Array.isArray(filter) ? filter.length > 0 : filter
      ),
    salespersonListSortOptions,
    handleAdd,
  };
};

export default useSalespersonListHandler;
